import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { unwrapApiObject } from "@/lib/api-response";
import { useAuthStore } from "@/store/authStore";

export type CvContact = {
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
};

export type CvEducation = {
  institution?: string;
  degree?: string;
  year?: string;
  detail?: string;
};

export type CvExperience = {
  placement_id?: number;
  role?: string;
  company?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  hours?: number;
  verified?: boolean;
  completed?: boolean;
  description?: string;
  highlights?: string[];
  skills?: string[];
  source?: string;
};

export type CvAchievement = {
  title?: string;
  issued_on?: string;
  cert_number?: string;
  source?: string;
};

export type StudentResume = {
  name?: string;
  objective?: string;
  contact?: CvContact;
  education?: CvEducation[];
  experience?: CvExperience[];
  projects?: CvAchievement[];
  skills?: string[];
  generated_at?: string;
  data_completeness?: string[];
};

/** CvOverrides are the free-text fields a student may add on top of the
 * generated CV. The verified sections are not represented here because the
 * server rebuilds them from platform records on every render. */
export type CvOverrides = {
  objective?: string;
  headline?: string;
  highlight_skills?: string[];
  extra_experience?: CvExperience[];
};

export const emptyResume: StudentResume = {
  name: "",
  objective: "",
  contact: {},
  education: [],
  experience: [],
  projects: [],
  skills: [],
  data_completeness: [],
};

/** useMyCV loads the assembled resume for the preview pane. */
export function useMyCV() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["my-cv"],
    queryFn: async () => {
      const res = await apiClient.get("/students/me/cv");
      return unwrapApiObject<StudentResume>(res, emptyResume);
    },
    enabled: !!user?.id,
  });
}

/**
 * useDownloadCV requests the PDF and hands the browser a blob URL.
 *
 * The overrides are sent to the server rather than applied in the browser so the
 * downloaded file is rendered from the same records as the preview. A plain
 * anchor href is not used because the endpoint needs the auth token.
 */
export function useDownloadCV() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (overrides: CvOverrides) => {
      const res = await apiClient.post("/students/me/cv/download", overrides, {
        responseType: "blob",
      });
      return res.data as Blob;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-cv"] });
    },
  });
}

/** downloadBlob saves a generated PDF using the student's own name. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers, so it is
  // deferred by a tick.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export type PlacementCompletion = {
  placement_id?: number;
  status?: string;
  hours_required?: number;
  hours_approved?: number;
  hours_remaining?: number;
  eligible?: boolean;
  certificate_issued?: boolean;
  cert_number?: string;
};

/** usePlacementCompletion reports how far a placement is from completable. */
export function usePlacementCompletion(placementId?: number) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["placement-completion", placementId],
    queryFn: async () => {
      const res = await apiClient.get(`/students/me/placement/${placementId}/completion`);
      return unwrapApiObject<PlacementCompletion>(res, {});
    },
    enabled: !!user?.id && !!placementId,
  });
}

/** useCompleteMyPlacement claims the completion certificate for a placement. */
export function useCompleteMyPlacement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (placementId: number) => {
      const res = await apiClient.post(`/students/me/placement/${placementId}/complete`, {});
      return unwrapApiObject<{ placement?: unknown; certificate?: unknown }>(res, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-placements"] });
      queryClient.invalidateQueries({ queryKey: ["placement-completion"] });
      queryClient.invalidateQueries({ queryKey: ["student-certificates"] });
      queryClient.invalidateQueries({ queryKey: ["my-cv"] });
    },
  });
}